import { ProductModal } from './product-modal'
import { BaseComponent } from '../../components/base-component'
import { CarouselComponent } from 'src/scripts/components/carousel'
import { $el, $elParent, whenDefined } from 'src/scripts/utils/utils'

export class ProductModalImageWrap extends BaseComponent {
  image: HTMLElement
  modal: ProductModal
  carousel: CarouselComponent
  preloader: HTMLElement
  currentSettedScale: number
  isDragging: boolean
  offsetX: number
  offsetY: number
  startDragPoint: { x: number; y: number }
  maxScale: number
  minScale: number
  isTapped: boolean
  tapTimeout: number
  lastGesture: string
  zoomTimeoutId: ReturnType<typeof setTimeout>
  isTappedTimeout: ReturnType<typeof setTimeout>
  isDoubleTapped: boolean
  firstMove: boolean
  fingers: number
  startMoveTouches: number
  lastDistanceBetweenTouches: number
  dragStartPointY: number
  dragStartPointX: number
  isPinchScaling: boolean

  constructor() {
    super()

    this.image = $el('img', this)
    this.modal = $elParent('product-modal', this)
    this.carousel = this.modal.carousel
    this.preloader = $el('[data-product-modal-image-preloader]', this)

    this.currentSettedScale = 1
    this.isDragging = false
    this.offsetX = 0
    this.offsetY = 0
    this.startDragPoint = { x: 0, y: 0 }
    this.maxScale = 3
    this.minScale = 1
    this.isTapped = false
    this.tapTimeout = 300
    this.lastGesture = null

    this.setZoomCursorVisible(true)
  }

  mountComponent() {
    this.addListener(this.image, 'mousedown', this.handleMouseDown)
    this.addListener(this.image, 'mouseup', this.handleMouseUp)
    this.addListener(this.image, 'mousemove', this.handleMouseMove)
    this.addListener(this.image, 'touchstart', this.handleTouchStart)
    this.addListener(this.image, 'touchend', this.handleTouchEnd)
    this.addListener(this.image, 'touchmove', this.handleTouchMove)
    this.addListener(this.image, 'load', this.handleImageLoad)
    whenDefined('product-modal').then(() => {
      this.modal.on('hide', this.handleModalHide)
    })
    this.carousel?.embla.on('select', this.handleCarouselSelect)
  }

  unmountComponent() {
    this.modal?.off('hide', this.handleModalHide)
    this.carousel?.embla.off('select', this.handleCarouselSelect)
    clearTimeout(this.zoomTimeoutId)
    clearTimeout(this.isTappedTimeout)
  }

  handleImageLoad = () => {
    this.setPreloaderVisible(false)
  }

  handleModalHide = () => {
    this.reset()
  }

  handleCarouselSelect = () => {
    this.reset()
  }

  handleTouchStart = event => {
    event.preventDefault()

    this.fingers = event.touches.length

    this.firstMove = true

    this.detectDoubleTap(event)
  }

  handleTouchMove = event => {
    event.preventDefault()

    if (!this.isDoubleTapped) {
      if (this.firstMove) {
        this.updateGesture(event)

        this.startMoveTouches = event.touches.length
      } else if (this.lastGesture) {
        switch (this.lastGesture) {
          case 'zoom':
            if (this.startMoveTouches === 2 && event.touches.length === 2) {
              this.pinch(event)
            }

            break
          case 'drag':
            if (this.startMoveTouches === 1 && event.touches.length === 1) {
              const { clientX, clientY } = event.targetTouches[0]

              this.drag(clientX, clientY)
            }

            break
        }
      } else {
        this.setGesture('none', event)
      }

      this.firstMove = false
    }
  }

  handleTouchEnd = event => {
    event.preventDefault()

    this.fingers = event.touches.length

    this.updateGesture(event)
  }

  handleMouseDown = event => {
    event.preventDefault()

    this.startDrag(event.clientX, event.clientY)

    this.setDragging(true)
  }

  handleMouseUp = event => {
    this.setDragging(false)

    const time = this.modal.isZoomed ? 75 : 0

    this.zoomTimeoutId = setTimeout(() => {
      this.touchZoom(event.clientX, event.clientY)
    }, time)
  }

  handleMouseMove = event => {
    event.preventDefault()

    clearTimeout(this.zoomTimeoutId)

    if (this.isDragging && this.modal.isZoomed) {
      const { clientX, clientY } = event

      this.drag(clientX, clientY)
    }
  }

  detectDoubleTap(event) {
    if (this.isTappedTimeout && this.fingers === 1) {
      clearTimeout(this.isTappedTimeout)

      this.isTappedTimeout = null

      this.isDoubleTapped = true

      this.touchZoom(
        event.changedTouches[0].clientX,
        event.changedTouches[0].clientY,
      )

      switch (this.lastGesture) {
        case 'zoom':
          this.setPinchScaling(false)

          break
        case 'drag':
          this.setDragging(false)

          break
      }
    } else {
      this.isTappedTimeout = setTimeout(() => {
        this.isTappedTimeout = null

        this.isDoubleTapped = false
      }, this.tapTimeout)
    }
  }

  updateGesture(event) {
    if (this.fingers === 2) {
      this.setGesture('zoom', event)
    } else if (this.fingers === 1) {
      this.setGesture('drag', event)
    } else {
      this.setGesture('none', event)
    }
  }

  setGesture(newGesture, event) {
    if (this.lastGesture !== newGesture) {
      if (this.lastGesture && !newGesture) {
        switch (this.lastGesture) {
          case 'zoom':
            this.setPinchScaling(false)
            break
          case 'drag':
            this.setDragging(false)
            break
        }
      }

      switch (newGesture) {
        case 'zoom':
          this.setPinchScaling(true)
          break
        case 'drag':
          this.startDrag(
            event.changedTouches[0].clientX,
            event.changedTouches[0].clientY,
          )
          this.setDragging(true)
          break
        case 'none':
          this.setDragging(false)
          this.setPinchScaling(false)
          break
      }
    }

    this.lastGesture = newGesture
  }

  pinch(event) {
    const distanceBetweenTouches = this.getDistanceBetweenTouches(event)
    const isZoomIn = distanceBetweenTouches > this.lastDistanceBetweenTouches
    const factor = isZoomIn ? 0.125 : -0.125
    const newScale = this.getNewScale(this.currentScale + factor)

    const midPoint = this.getTouchCenter(event)

    const xs = (midPoint.x - this.offsetX) / this.currentScale
    const ys = (midPoint.y - this.offsetY) / this.currentScale

    this.lastDistanceBetweenTouches = distanceBetweenTouches

    if (!isZoomIn && newScale === 1) {
      this.reset()
      return
    }

    this.setTransform({
      scale: newScale,
      offsetX: midPoint.x - xs * newScale,
      offsetY: midPoint.y - ys * newScale,
    })
  }

  getDistanceBetweenTouches(event) {
    return Math.hypot(
      event.touches[0].pageX - event.touches[1].pageX,
      event.touches[0].pageY - event.touches[1].pageY,
    )
  }

  startDrag(clientX, clientY) {
    this.startDragPoint = {
      x: clientX - this.offsetX,
      y: clientY - this.offsetY,
    }

    this.dragStartPointX = clientX
    this.dragStartPointY = clientY
  }

  drag(clientX, clientY) {
    const isRightDrag = this.dragStartPointX > clientX
    const isBottomDrag = this.dragStartPointY > clientY

    const newScale = this.getNewScale(this.currentScale)
    let newOffsetX = clientX - this.startDragPoint.x
    let newOffsetY = clientY - this.startDragPoint.y

    const isIntersectBounds = this.getIntersectBounds({
      isRightDrag,
      isBottomDrag,
    })

    if (isIntersectBounds.right || isIntersectBounds.left) {
      newOffsetX = this.offsetX
    }

    if (isIntersectBounds.bottom || isIntersectBounds.top) {
      newOffsetY = this.offsetY
    }

    this.setTransform({
      scale: newScale,
      offsetX: newOffsetX,
      offsetY: newOffsetY,
    })
  }

  getIntersectBounds({ isRightDrag, isBottomDrag }) {
    const imageRect = this.image.getBoundingClientRect()

    const delta = 8
    const deltaX = isRightDrag ? -1 * delta : delta
    const deltaY = isBottomDrag ? -1 * delta : delta

    const right = isRightDrag && imageRect.right + deltaX < window.innerWidth
    const left = !isRightDrag && imageRect.left + deltaX > 0
    const bottom =
      isBottomDrag && imageRect.bottom + deltaY < window.innerHeight
    const top = !isBottomDrag && imageRect.top + deltaY > 0

    return {
      right,
      left,
      bottom,
      top,
    }
  }

  getTouchCenter(event) {
    const sum = (a, b) => a + b

    const touches = [...event.touches]
    const touchCount = touches.length

    return {
      x: touches.map(touch => touch.pageX).reduce(sum) / touchCount,
      y: touches.map(touch => touch.pageY).reduce(sum) / touchCount,
    }
  }

  setTransform({ offsetX, offsetY, scale }) {
    this.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) scale3d(${scale}, ${scale}, 1)`

    this.offsetX = offsetX
    this.offsetY = offsetY
    this.currentScale = scale
  }

  touchZoom(clientX, clientY) {
    const xs = (clientX - this.offsetX) / this.currentScale
    const ys = (clientY - this.offsetY) / this.currentScale

    let newScale
    let newOffsetX
    let newOffsetY

    if (this.modal.isZoomed) {
      newScale = this.getNewScale(1)
      newOffsetX = 0
      newOffsetY = 0
    } else {
      newScale = this.getNewScale(3)
      newOffsetX = clientX - xs * newScale
      newOffsetY = clientY - ys * newScale
    }

    this.style.cursor = this.modal.isZoomed ? '' : 'grab'
    this.setZoomCursorVisible(this.modal.isZoomed)

    this.setTransform({
      scale: newScale,
      offsetX: newOffsetX,
      offsetY: newOffsetY,
    })
  }

  reset() {
    this.setTransform({
      scale: 1,
      offsetX: 0,
      offsetY: 0,
    })
  }

  getNewScale(newScale) {
    return Math.max(this.minScale, Math.min(this.maxScale, newScale))
  }

  setPinchScaling(isPinchScaling) {
    this.toggleAttribute('is-pinch-scaling', isPinchScaling)

    this.isPinchScaling = isPinchScaling
  }

  setDragging(isDragging) {
    this.toggleAttribute('is-dragging', isDragging)

    this.isDragging = isDragging
  }

  setZoomCursorVisible(isVisible) {
    this.image?.toggleAttribute('data-zoom-cursor-target', isVisible)
  }

  setModalZoom(isZoomed) {
    this.modal.isZoomed = isZoomed

    if (this.modal.isZoomed) {
      this.modal.zoomedImage = this
    }

    this.modal.updateModal()
  }

  setPreloaderVisible(isVisible) {
    this.preloader.classList.toggle('hidden', !isVisible)
    this.image?.toggleAttribute('is-loaded', !isVisible)
  }

  set currentScale(value) {
    if (this.currentSettedScale !== value) {
      this.setModalZoom(value > 1)

      this.currentSettedScale = value
    }
  }

  get currentScale() {
    return this.currentSettedScale
  }
}
